import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Spinner from './Spinner';
import { sendEvent } from './utils/analytics';

const LatestSpotifyReleases = () => {
  // Move these into useMemo to avoid recreation on each render
  const config = useMemo(() => ({
    artistId: '05tmGPn4fFdVpnsMt0YW5S',
    cacheKey: 'spotifyReleasesCache',
    cacheDuration: 15 * 24 * 60 * 60 * 1000, // 15 days in milliseconds
    baseTitle: 'Music | Adam Ten',
    description: 'Stream the latest releases from Adam Ten - featuring new singles, remixes, and albums on major streaming platforms.',
    currentUrl: 'https://www.adam-ten.com/music'
  }), []);

  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retryCount, setRetryCount] = useState(0);

  // Track modal opening (only if consent is given)
  useEffect(() => {
    sendEvent({
      category: 'Modal',
      action: 'Opened Spotify Releases Modal',
      label: 'Spotify Modal',
    });
  }, []);

  // Fetch releases via backend proxy (Spotify blocks direct browser requests with 403)
  useEffect(() => {
    const fetchReleases = async () => {
      try {
        // Check cached data first
        const cachedData = localStorage.getItem(config.cacheKey);
        if (cachedData) {
          const parsedData = JSON.parse(cachedData);
          const isCacheValid = Date.now() - parsedData.timestamp < config.cacheDuration;

          if (isCacheValid) {
            setReleases(parsedData.releases);
            setLoading(false);
            return;
          }
          localStorage.removeItem(config.cacheKey);
        }

        const response = await fetch('/api/spotify-releases');
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || `Request failed: ${response.status}`);
        }

        const { releases: sortedReleases } = await response.json();

        // Cache the results
        const dataToCache = {
          releases: sortedReleases,
          timestamp: Date.now(),
        };
        localStorage.setItem(config.cacheKey, JSON.stringify(dataToCache));

        setReleases(sortedReleases);
        setLoading(false);
        setError('');
      } catch (err) {
        console.error('Failed to fetch releases:', err);

        if (err.message.includes('Rate limit') || err.message.includes('429')) {
          setError('Too many requests. Please try again in a minute.');
        } else if (err.message.includes('Unauthorized') || err.message.includes('401')) {
          setError('Authentication error. Please refresh the page.');
        } else {
          setError('Failed to load releases. Please try again later.');
        }

        // Use cached data as fallback if available
        const cachedData = localStorage.getItem(config.cacheKey);
        if (cachedData) {
          const parsedData = JSON.parse(cachedData);
          setReleases(parsedData.releases);
          setError('Using cached data. ' + err.message);
        }

        setLoading(false);
      }
    };

    if (retryCount < 3) {
      fetchReleases();
    }
  }, [config, retryCount]);

  // Add retry functionality
  const handleRetry = () => {
    setLoading(true);
    setError('');
    setRetryCount(prev => prev + 1);
  };

  if (loading) {
    return <div><Spinner /></div>;
  }

  return (
    <div className="releases-modal max-width">
      <Helmet>
        <title>{config.baseTitle}</title>
        <meta name="description" content={config.description} />
        <meta property="og:title" content={config.baseTitle} />
        <meta property="og:description" content={config.description} />
        <meta name="twitter:title" content={config.baseTitle} />
        <meta name="twitter:description" content={config.description} />
        <link rel="canonical" href={config.currentUrl} />
        <meta property="og:url" content={config.currentUrl} />
      </Helmet>

      <h2>Latest Releases</h2>
      {error && (
        <div className="error-message">
          <p>{error}</p>
          {retryCount < 3 && (
            <button onClick={handleRetry} className="retry-button">
              Try Again
            </button>
          )}
        </div>
      )}
      
      {releases.length > 0 ? (
        <ul>
          {releases.map((release) => (
            <li key={release.id}>
              <a
                href={release.external_urls.spotify}
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className="left">
                  <img
                    src={release.images[0]?.url || 'https://via.placeholder.com/240'}
                    alt={release.name}
                    width="240"
                    height="240"
                  />
                  <span className="word-play"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="#fff" viewBox="0 0 256 256"><path d="M232.4,114.49,88.32,26.35a16,16,0,0,0-16.2-.3A15.86,15.86,0,0,0,64,39.87V216.13A15.94,15.94,0,0,0,80,232a16.07,16.07,0,0,0,8.36-2.35L232.4,141.51a15.81,15.81,0,0,0,0-27ZM80,215.94V40l143.83,88Z"></path></svg>Play</span>
                </div>
                <div className="right">
                  <h3>{release.name}</h3>
                  <span>{[release.label, new Date(release.release_date).getFullYear()].filter(Boolean).join(' · ')}</span>
                </div>
              </a>
            </li>
          ))}
        </ul>
      ) : !error && (
        <p>No releases found</p>
      )}

      <a 
        href={`https://open.spotify.com/artist/${config.artistId}/discography/all`} 
        target="_blank" 
        rel="noopener noreferrer" 
        className="bottom-link"
      >
        View more releases
      </a>
    </div>
  );
};

export default LatestSpotifyReleases;